import type {CustomDealProduct} from '../../../types';

import {Loading} from '@ds/components';
import Empty from 'common/empty/Empty';
import {useEffect, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useAppDispatch, useAppSelector} from 'store';
import {
  getDealProducts,
  getInProgressCustomDeals,
  resetDealProducts
} from 'store/slices/custom-deal';

import {CUSTOM_DEAL_PAGE_SIZE} from '../../../constants';
import {getCustomDealVendorId} from '../../../utils';
import DealDetailFilters from './components/DealDetailFilters/DealDetailFilters';
import DealDetailProductsListItem from './components/DealDetailProductsListItem/DealDetailProductsListItem';
import DealDetailProductsListItemSkeleton from './components/DealDetailProductsListItemSkeleton/DealDetailProductsListItemSkeleton';

type DealDetailProductsListProps = {
  dealId?: string;
};

type DealDetailListFilters = {
  search: string | null;
  stock_status: string | number | null;
  sort: string | null;
  badge: string | number | null;
  categories: number[];
};

const DealDetailProductsList: React.FC<DealDetailProductsListProps> = ({dealId}) => {
  const [pagination, setPagination] = useState({page: 1, pageSize: CUSTOM_DEAL_PAGE_SIZE});
  const [filters, setFilters] = useState<DealDetailListFilters>({
    search: null,
    stock_status: null,
    sort: null,
    badge: null,
    categories: []
  });
  const vendorId = getCustomDealVendorId();
  const {dealProducts, dealProductsLoading, dealCategories} = useAppSelector(
    (state) => state.customDeal
  );
  const dispatch = useAppDispatch();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dealId) {
      return;
    }
    dispatch(resetDealProducts());
    void dispatch(getInProgressCustomDeals({vendorId}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getItems = ({pageSize, page}: {pageSize?: number; page?: number}) => {
    if (!dealId) {
      return;
    }

    const preparedFilters: Record<string, unknown> = {
      deal_id: dealId,
      vendor_id: vendorId,
      page_size: pageSize ? pageSize : pagination.pageSize,
      page: page ? page : pagination.page,
      search: filters.search,
      stock_status: filters.stock_status,
      badge: filters.badge,
      sort: filters.sort,
      parent_category_ids: filters.categories.length > 0 ? filters.categories : null
    };

    Object.keys(preparedFilters).forEach((key) => {
      if (preparedFilters[key] === null || preparedFilters[key] === undefined) {
        delete preparedFilters[key];
      }
    });

    void dispatch(getDealProducts(preparedFilters));
  };

  const loadMore = () => {
    if (!dealId) {
      return;
    }
    getItems({page: pagination.page + 1});
    setPagination({pageSize: pagination.pageSize, page: pagination.page + 1});
  };

  useEffect(() => {
    if (dealId) {
      getItems({pageSize: pagination.pageSize, page: 1});
      setPagination({page: 1, pageSize: pagination.pageSize});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dealId]);

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <DealDetailFilters
        filters={filters}
        onChangeFilter={(name, value) => {
          setFilters((current) => ({...current, [name]: value}));
        }}
        dealCategories={dealCategories}
      />
      {!dealProductsLoading && dealProducts.current === undefined ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Empty>
            <p>هیچ کالایی برای نمایش وجود ندارد</p>
          </Empty>
        </div>
      ) : null}
      <div
        id="selectProductsListWrapper"
        ref={listRef}
        className="relative min-h-0 flex-1 overflow-y-auto pb-12.5 px-4"
      >
        <InfiniteScroll
          scrollableTarget="selectProductsListWrapper"
          scrollThreshold={0.8}
          pullDownToRefreshContent={<></>}
          pullDownToRefreshThreshold={50}
          next={loadMore}
          hasMore={dealProducts?.current?.length < dealProducts?.total_items}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
          dataLength={dealProducts?.current?.length || 0}
        >
          {dealProductsLoading && (!dealProducts?.current || dealProducts.current.length === 0) ? (
            Array.from({length: 5}).map((_, index) => (
              <DealDetailProductsListItemSkeleton key={`skeleton-${index}`} />
            ))
          ) : (
            dealProducts?.current?.map((item: CustomDealProduct) => (
              <DealDetailProductsListItem key={item.id} item={item} />
            ))
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default DealDetailProductsList;
